import type { SummaryMonth } from '../types.js'
import { TIER_HEX, TIER_LABELS } from '../../../modules/core/index.js'
import { MONTH_STATE_LABELS } from '../labels.js'

/** Geometry of the chart, in SVG units; the viewBox scales it to the page. */
const WIDTH = 720
const HEIGHT = 200
const PADDING = { top: 22, right: 12, bottom: 34, left: 34 }
const GRID = [0, 25, 50, 75, 100]
const UNSCORED = '#9aa9b1'

const plotWidth = WIDTH - PADDING.left - PADDING.right
const plotHeight = HEIGHT - PADDING.top - PADDING.bottom
const y = (score: number): number => PADDING.top + plotHeight - (score / 100) * plotHeight

/**
 * The span's months as a picture above the table: one column per month,
 * its height the final score, its colour the tier the close set. A month
 * with no score is a dotted mark with its state, so a gap reads as a gap
 * and not as a zero. Plain SVG, so it prints as it shows.
 */
export function SummaryScoreChart({ months }: { months: readonly SummaryMonth[] }) {
  if (months.length === 0) {
    return null
  }
  const slot = plotWidth / months.length
  const barWidth = Math.min(40, slot * 0.6)
  const scored = months.filter((month) => month.score !== null)
  const title = `Final score by month, ${months[0]?.period ?? ''} to ${months[months.length - 1]?.period ?? ''}`

  return (
    <figure className="mb-3">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={title} className="w-full h-auto">
        <title>{title}</title>
        {GRID.map((value) => (
          <g key={value}>
            <line x1={PADDING.left} x2={WIDTH - PADDING.right} y1={y(value)} y2={y(value)} stroke="#0A2830" strokeOpacity={value === 0 ? 0.5 : 0.12} strokeWidth={1} />
            <text x={PADDING.left - 6} y={y(value) + 3} textAnchor="end" fontSize={9} fill="#2C5060">
              {value}
            </text>
          </g>
        ))}
        {months.map((month, index) => {
          const centre = PADDING.left + slot * index + slot / 2
          const label = month.period.slice(5, 7)
          if (month.score === null) {
            return (
              <g key={month.period}>
                <circle cx={centre} cy={y(0) - 4} r={2.5} fill={UNSCORED} />
                <text x={centre} y={y(0) - 10} textAnchor="middle" fontSize={7.5} fill={UNSCORED}>
                  {MONTH_STATE_LABELS[month.state].split(',')[0]}
                </text>
                <text x={centre} y={HEIGHT - PADDING.bottom + 14} textAnchor="middle" fontSize={9} fill="#2C5060">
                  {label}
                </text>
              </g>
            )
          }
          const colour = month.tier ? TIER_HEX[month.tier] : '#0C5E74'
          const top = y(month.score)
          return (
            <g key={month.period}>
              <rect x={centre - barWidth / 2} y={top} width={barWidth} height={y(0) - top} fill={colour} rx={2}>
                <title>{`${month.period}: ${month.score}${month.tier ? `, ${TIER_LABELS[month.tier]}` : ''}${month.provisional ? ' (provisional)' : ''}`}</title>
              </rect>
              <text x={centre} y={top - 5} textAnchor="middle" fontSize={9} fontWeight={700} fill="#0A2830">
                {month.score}
              </text>
              <text x={centre} y={HEIGHT - PADDING.bottom + 14} textAnchor="middle" fontSize={9} fill="#2C5060">
                {label}
              </text>
            </g>
          )
        })}
        <text x={PADDING.left} y={HEIGHT - 4} fontSize={8} fill="#2C5060">
          {months[0]?.period.slice(0, 4)}
          {months[0]?.period.slice(0, 4) !== months[months.length - 1]?.period.slice(0, 4) ? ` to ${months[months.length - 1]?.period.slice(0, 4)}` : ''} · month of the span
        </text>
      </svg>
      {scored.length > 0 && (
        <figcaption className="flex flex-wrap gap-x-3 gap-y-1 text-[9px] text-ink-mid">
          {(['gold', 'silver', 'bronze', 'probationary'] as const)
            .filter((tier) => scored.some((month) => month.tier === tier))
            .map((tier) => (
              <span key={tier} className="inline-flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: TIER_HEX[tier] }} />
                {TIER_LABELS[tier]} at close
              </span>
            ))}
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: UNSCORED }} />
            no final score
          </span>
        </figcaption>
      )}
    </figure>
  )
}
